// Tossit WebView <-> Native location bridge helper.
// When the web app runs inside the Tossit native app, window.TossitNative is injected.
// In a normal browser it is absent, so always feature-detect first.

// Detects the Tossit native WebView. Primary signal is the injected bridge
// (window.TossitNative.isAvailable). Falls back to a custom User-Agent token
// ("TossitApp") which survives reloads/navigations even if the bridge injects late.
export const isTossitNativeApp = () => {
    if (typeof window === 'undefined') return false;
    if (window.TossitNative && window.TossitNative.isAvailable === true) return true;
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    return ua.includes('TossitApp');
};

// Requests location via the native bridge. Never rejects — always resolves with the
// bridge response object: { status, latitude, longitude, accuracy, address, error }.
// status: "granted" | "denied" | "blocked" | "error"
export const getNativeLocation = async (options = { highAccuracy: false }) => {
    return window.TossitNative.getLocation(options);
};

// Silent location: returns { lat, lng, address? } ONLY when permission is already
// granted — it never triggers a permission prompt. Resolves null otherwise.
export const getSilentLocation = async () => {
    try {
        if (isTossitNativeApp() && window.TossitNative && typeof window.TossitNative.getLocation === 'function') {
            const res = await window.TossitNative.getLocation({ highAccuracy: false });
            if (res && res.status === 'granted' && typeof res.latitude === 'number') {
                return { lat: res.latitude, lng: res.longitude, address: res.address };
            }
            return null;
        }
        if (!navigator.geolocation || !navigator.permissions) return null;
        const perm = await navigator.permissions.query({ name: 'geolocation' });
        if (perm.state !== 'granted') return null;
        return await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                () => resolve(null),
                { enableHighAccuracy: false, timeout: 8000 }
            );
        });
    } catch {
        return null;
    }
};

export const openNativeAppSettings = () => {
    if (isTossitNativeApp() && typeof window.TossitNative.openAppSettings === 'function') {
        window.TossitNative.openAppSettings();
    }
};

// Reverse-geocode coordinates to a readable { title, subtitle } using the Google
// Maps JS Geocoder when available. Resolves null if Maps isn't loaded or fails.
export const reverseGeocodeLatLng = async (lat, lng) => {
    try {
        if (!(window.google && window.google.maps && window.google.maps.Geocoder)) return null;
        const geocoder = new window.google.maps.Geocoder();
        return await new Promise((resolve) => {
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    resolve({
                        title: results[0].address_components?.[0]?.long_name || 'Current location',
                        subtitle: results[0].formatted_address,
                    });
                } else {
                    resolve(null);
                }
            });
        });
    } catch {
        return null;
    }
};

// Live location: subscribe to continuous updates from the native host.
// Native contract: window.TossitNative.watchLocation(options, callback) -> watchId,
// callback receives { status, latitude, longitude, accuracy }.
// Returns the watchId (or null if unsupported).
export const watchNativeLocation = (onUpdate, options = { highAccuracy: true }) => {
    if (!isTossitNativeApp() || typeof window.TossitNative.watchLocation !== 'function') return null;
    try {
        return window.TossitNative.watchLocation(options, onUpdate);
    } catch (e) {
        return null;
    }
};

export const clearNativeLocationWatch = (watchId) => {
    if (isTossitNativeApp() && typeof window.TossitNative.clearLocationWatch === 'function') {
        try { window.TossitNative.clearLocationWatch(watchId); } catch (e) { /* noop */ }
    }
};
